/**
 * SP4 — Disponibilité des pages et capacités eBusiness par tenant : LOGIQUE PURE.
 *
 * Source unique partagée par les trois consommateurs du Front (le service
 * injectable, la garde de route, le menu). Aucune dépendance Angular : la
 * fonction de menu est pure et testée par égalité profonde sur une fixture VGE,
 * elle ne peut pas injecter de service.
 *
 * MIROIR de `Api/common/tenant/ebusinessConfig.js` — mêmes défauts, mêmes règles
 * de lecture. Toute évolution doit être portée des deux côtés, sinon une page
 * masquée dans le navigateur resterait ouverte côté API (ou l'inverse).
 *
 * DEUX NATURES DE RÉGLAGE, DEUX DÉFAUTS :
 *  • CAPACITÉ = un comportement consommé en plusieurs points (crons, hooks de
 *    sauvegarde, blocs dans les écrans de factures et de dépenses). Clé absente
 *    → vge uniquement : l'oubli d'une clé ne doit jamais ouvrir un comportement
 *    à VGI/VGS.
 *  • PAGE = un écran, fermé en un seul point. Clé absente → disponible, comme
 *    `tabEnabled()` / `moduleEnabled()` : VGE n'a rien à déclarer pour rester
 *    identique à lui-même.
 */

/** Bloc `MODULES.EBUSINESS` tel que servi par /parameters. */
export interface BlocEbusiness {
  facturationAuto?: any;
  rapprochementBancaire?: any;
  pages?: { [pageId: string]: boolean };
  [autre: string]: any;
}

/**
 * Pages dont la disponibilité est gouvernée par une CAPACITÉ et non par la liste
 * `pages` : la capacité vaut aussi ailleurs dans le module (le rapprochement
 * bancaire s'affiche jusque dans les modales de factures et de dépenses).
 */
export const CAPACITE_PAR_PAGE: { [pageId: string]: string } = {
  'rapprochement-bancaire': 'rapprochementBancaire',
};

/**
 * Pages eBusiness qui n'ont de sens que si un MODULE est ouvert pour la
 * plateforme. Le pont eBusiness → ULTRAnet (types d'audit, table
 * `intra_eb_category_audit`) est VGE-only : « le module ULTRAnet avec les audits
 * n'est que pour VGE, le pont eBusiness → ULTRAnet = que pour VGE »
 * (client 2026-07-26).
 *
 * On réutilise ici le gate de module DÉJÀ déclaré dans `environment.<tenant>.ts`
 * (`modules.ULTRAnet.enabled`) plutôt que d'inventer un second réglage : VGI et
 * VGS ferment déjà MULTInet et ULTRAnet depuis SP1.
 */
export const MODULE_PAR_PAGE: { [pageId: string]: string } = {
  'typesaudit': 'ULTRAnet',
  'typesaudit/edit': 'ULTRAnet',
};

/** Préfixe des routes du module, retiré pour obtenir l'identifiant de page. */
const RACINE_EBUSINESS = 'eBusiness';

/**
 * Chemin de route → identifiant de page : les segments paramétrés sont retirés,
 * de sorte que les variantes d'un même écran partagent UNE clé de configuration.
 *   'factures/previsionnelBudgetaire'      ─┐
 *   'factures/previsionnelBudgetaire/:ido' ─┴─→ « factures/previsionnelBudgetaire »
 */
export function pageIdDepuisChemin(chemin: string | null | undefined): string {
  return (chemin ?? '')
    .split('/')
    .filter((segment) => segment.length > 0 && segment.charAt(0) !== ':')
    .join('/');
}

/**
 * `routerLink` de menu → identifiant de page, ou null si le lien ne pointe pas
 * dans le module eBusiness (les autres entrées du menu ne sont pas concernées).
 */
export function pageIdDepuisLien(routerLink: any): string | null {
  if (!Array.isArray(routerLink)) { return null; }
  const segments = routerLink
    .filter((s) => typeof s === 'string')
    .join('/')
    .split('/')
    .filter((s) => s.length > 0);
  if (segments[0] !== RACINE_EBUSINESS) { return null; }
  return pageIdDepuisChemin(segments.slice(1).join('/'));
}

/**
 * Une CAPACITÉ est-elle active ? Lecture STRICTE : seuls `true` et `1` activent.
 * Toute autre valeur désactive — une config saisie à la main ne doit pas
 * déclencher un comportement par accident de typage (`"false"` est une chaîne
 * vraie en JavaScript). Clé absente → `vge` uniquement.
 */
export function capaciteActive(bloc: BlocEbusiness | null | undefined, tenantKey: string, nom: string): boolean {
  if (bloc && Object.prototype.hasOwnProperty.call(bloc, nom)) {
    return bloc[nom] === true || bloc[nom] === 1;
  }
  return tenantKey === 'vge';
}

/** Une PAGE est-elle déclarée disponible ? Seul un `false` explicite ferme. */
export function pageDeclareeActive(bloc: BlocEbusiness | null | undefined, pageId: string): boolean {
  const pages = bloc?.pages;
  if (!pages || typeof pages !== 'object') { return true; }
  return pages[pageId] !== false;
}

/**
 * Décision finale pour une page, dans cet ordre :
 *   1. le MODULE dont elle dépend est-il ouvert pour la plateforme ? (veto)
 *   2. si elle relève d'une CAPACITÉ, c'est la capacité qui tranche ;
 *   3. sinon, la liste `pages` de la configuration.
 *
 * `moduleEnabled` est passé en paramètre (et non lu depuis un service) pour que
 * cette fonction reste pure : le menu, qui l'utilise, est lui-même une fonction
 * pure verrouillée par une fixture. Omis → aucun veto de module, ce qui préserve
 * le comportement des appelants qui n'en ont pas besoin.
 */
export function pageAutorisee(
  bloc: BlocEbusiness | null | undefined,
  tenantKey: string,
  pageId: string,
  moduleEnabled?: (moduleKey: string) => boolean,
): boolean {
  const module = MODULE_PAR_PAGE[pageId];
  if (module && moduleEnabled && !moduleEnabled(module)) { return false; }

  const capacite = CAPACITE_PAR_PAGE[pageId];
  return capacite
    ? capaciteActive(bloc, tenantKey, capacite)
    : pageDeclareeActive(bloc, pageId);
}

/** Extrait le bloc `MODULES.EBUSINESS` d'une réponse /parameters. */
export function blocEbusiness(parametres: any): BlocEbusiness {
  return parametres?.VGE?.MODULES?.EBUSINESS ?? {};
}
